import { AuthFetchError } from '@e3dc-react/shell/Types/api'
import { ApiResult } from 'src/Libs/api'
import { handleFetchError } from 'src/Libs/api/utils'
import { AuthFetchWithResponse } from 'src/Types/api'
import { FetchedUserDataConnected } from 'src/Libs/user/models/user.model'
import { fetchUsersWithConnections } from './user.api'

const HAGER_USER_DETAILS_FIELDS = [
  'user.ID',
  'user.username',
  'location.Email',
  'location.Telefon',
  'location.PlaceOR',
  'location.PLZ',
  'location.Address',
  'location.Land',
].join(',')

/**
 * Resolves a Hager user's username to the connected user details (numeric Portal user ID and
 * contact/location data).
 *
 * For Hager users the account link `accountId` is a UUID (ssoId), so the numeric user ID has to
 * be resolved from the username instead. Returns `null` when the username cannot be resolved to
 * exactly one user (no match or an ambiguous match), so the caller can degrade gracefully.
 */
export const fetchHagerUserDetails = async (
  username: string | undefined,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<FetchedUserDataConnected | null, AuthFetchError | Error>> => {
  try {
    if (!username) {
      throw new Error('Installer username is missing')
    }

    const result = await fetchUsersWithConnections(
      {
        connect: 'location',
        fields: HAGER_USER_DETAILS_FIELDS,
        filter: `user.username=${username}`,
      },
      authFetch
    )

    if (result.hasErrors) {
      throw result.error ?? new Error('Could not fetch the Hager user details')
    }

    if (!result.value || result.value.length !== 1) {
      return new ApiResult<FetchedUserDataConnected | null, AuthFetchError | Error>(null)
    }

    return new ApiResult<FetchedUserDataConnected | null, AuthFetchError | Error>(result.value[0])
  } catch (error) {
    return handleFetchError<FetchedUserDataConnected | null>(error)
  }
}

/**
 * Resolves a Hager owner's username to the numeric Portal user ID.
 * The installation's UUID cannot reliably be matched against user.ssoId.
 */
export const fetchHagerUserAccountId = async (
  username: string | undefined,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<number, AuthFetchError | Error>> => {
  try {
    if (!username) {
      throw new Error('Owner username is missing')
    }

    const result = await fetchUsersWithConnections(
      {
        connect: 'location',
        fields: 'user.ID',
        filter: `user.username=${username}`,
      },
      authFetch
    )

    if (result.hasErrors) {
      throw result.error ?? new Error('Could not fetch the Hager user account ID')
    }

    if (!result.value) {
      throw new Error('The Hager user lookup returned no data')
    }

    if (result.value.length === 0) {
      return new ApiResult<number, AuthFetchError | Error>()
    }

    if (result.value.length > 1) {
      throw new Error('Multiple users match the owner username')
    }

    const accountId = result.value[0]['user.ID']
    if (!Number.isSafeInteger(accountId) || accountId <= 0) {
      throw new Error('The Hager user lookup returned an invalid user ID')
    }

    return new ApiResult(accountId)
  } catch (error) {
    return handleFetchError<number>(error)
  }
}
